<script setup lang="ts">
/**
 * Virtual Profiler's own navigation: a floating pill with one link per section.
 *
 * The sidebar lists modules, not screens, so this is how the queue, the videos, the groups and
 * the configurations are reached once inside the module. What it lists, and which tab a route
 * belongs to, lives in `lib/vp-sections.ts`; this file only draws it.
 *
 * ## One header for the whole module, drawn by the shell
 *
 * No screen renders it. Every Virtual Profiler route names it in its meta (`sectionNav`, through
 * `section-nav.ts`) and the shell draws it once, above the page and outside the page transition.
 * Drawn by each screen, a detail brought its own copy sliding in over the list's and the
 * navigation looked like it moved; drawn by the shell, it stays still while only the page under
 * it slides. The routes stay flat — a layout route would turn a detail's slide into the slide of a
 * child inside a parent that never moves — and the configuration editor, a child dialog over the
 * list, inherits the list's meta.
 *
 * ## Why not `ArTabs`
 *
 * These are links to other screens, not panels of this one. `ArTabs` brings `tablist`/`tab`
 * semantics and arrow-key roving focus, which promise a screen reader that the content swaps in
 * place — and the group and video details already use `ArTabs` for exactly that, one level down.
 * Two tab strips stacked on a detail would be two claims of the same kind about different things.
 * Plain links with `aria-current` say what is true: `page` on the list the tab opens, `true` on a
 * detail under it.
 *
 * ## Why the queue signal is live
 *
 * The Cola tab shows how many runs wait in the queue and a dot when something runs or needs
 * looking at. It reads the queue itself, with the queue screen's own query and live connection
 * (`useQueueRealtime`, one socket shared by every screen that asks for it), so on every Virtual
 * Profiler screen it moves when the queue moves. It used to read only the cache, to spare the
 * other screens the queue read, and away from the queue it showed a number that no longer was.
 *
 * ## The hub is the icon, and on the hub the nav opens up
 *
 * The hub has no tab (product owner, 2026-10-14): the module's icon on the left is its link. On the
 * hub the nav widens to carry what the hub's hero card used to — the module's name, whether the
 * screen is live, and the reading options — and the card is gone. Everywhere else it folds back to
 * the icon and the tabs. The overview stays in `VP_SECTIONS` so the hub's route still belongs to a
 * section; it is only not drawn as a tab.
 */
import { computed, onMounted, useTemplateRef, type Component } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import {
  ArInfoTip,
  ArSeparator,
  ArStatusBadge,
  ArTooltip,
  ArTooltipContent,
  ArTooltipTrigger,
} from '@beonit/adaptive-raindrop';
import {
  Database,
  Gauge,
  LayoutDashboard,
  ListOrdered,
  SlidersHorizontal,
  UserSearch,
} from '@lucide/vue';
import { useAuthStore } from '@shared/presentation/auth/store.js';
import { formatCount } from '../lib/format-count.js';
import {
  ariaCurrentFor,
  type QueueTabSignal,
  queueTabSignal,
  sectionAccessibleName,
  VP_SECTIONS,
  type VpSectionKey,
  visibleVpSections,
} from '../lib/vp-sections.js';
import HubOptions from '../overview/presentation/HubOptions.vue';
import { useGlobalMetrics } from '../overview/use-global-metrics.js';
import { usePreferredMethod } from '../overview/use-preferred-method.js';
import { useAnalysisQueue } from '../queries.js';
import { describeRealtimeStatus } from '../queue/realtime-connection.js';
import { useQueueRealtime } from '../queue/use-queue-realtime.js';
import VpBackdrop from './VpBackdrop.vue';

// The «Métricas globales» and preferred-method choices, read on every screen of the module before
// any figure is painted.
useGlobalMetrics();
usePreferredMethod();

const route = useRoute();
const router = useRouter();
const auth = useAuthStore();

const { data, dataUpdatedAt, isError } = useAnalysisQueue();

// Held for as long as the nav is on screen: every queue event refetches the query above.
const realtime = useQueueRealtime();

/** The overview's own section: the icon's link, and the screen where the nav opens up. */
const OVERVIEW = VP_SECTIONS.find((entry) => entry.key === 'overview')!;

const onHub = computed(() => route.name === OVERVIEW.routeName);

const hubCurrent = computed(() => ariaCurrentFor(OVERVIEW, route.name));

/**
 * Whether what the hub shows is arriving live. Told when the queue read fails too: «Conectado»
 * above a queue that could not be read would tell somebody looking at empty tiles that all is well.
 */
const live = computed(() =>
  describeRealtimeStatus(realtime.status.value, realtime.error.value, isError.value),
);

/**
 * Read from the route, like the sidebar: a tab the guard would bounce is not drawn. Recomputed
 * from the session, so a revoked permission takes the tab away on the next render.
 */
const sections = computed(() =>
  visibleVpSections(
    VP_SECTIONS,
    (name) => router.getRoutes().find((record) => record.name === name)?.meta,
    auth.can,
  ),
);

// Live, so the read's age is never a reason to go quiet; a failed read still is.
const signal = computed(() =>
  queueTabSignal(data.value, dataUpdatedAt.value, dataUpdatedAt.value, {
    live: true,
    failed: isError.value,
  }),
);

/** Value imports: rendered through `<component :is>`, a type-only import renders nothing. */
const ICONS: Record<VpSectionKey, Component> = {
  overview: LayoutDashboard,
  queue: ListOrdered,
  videos: Database,
  groups: Gauge,
  configs: SlidersHorizontal,
};

/**
 * Plain class lists, joined by Vue and not by `cn`: tailwind-merge does not know the DS's type
 * scale, reads `text-ui-panel` as a colour and drops it next to `text-muted-foreground`. Nothing
 * here needs merging — the idle and active sets never set the same property as the base.
 *
 * The focus ring is inset because the list scrolls sideways, and a scroll container clips an
 * outer ring on every side.
 */
const TAB =
  'inline-flex h-8 items-center gap-2 whitespace-nowrap rounded-control px-3 text-ui-panel font-medium outline-none transition-colors focus-visible:ring-[3px] focus-visible:ring-inset focus-visible:ring-ring/50';
const TAB_IDLE = 'text-muted-foreground hover:bg-accent hover:text-foreground';
const TAB_ACTIVE = 'bg-p-accent/10 text-foreground ring-1 ring-inset ring-p-accent/20';

const PILL = 'min-w-5 rounded-pill px-1.5 text-center text-ui-tag leading-5 tabular-nums';

/**
 * Colour only as a dot. Amber as text falls below the contrast floor (see `kpi-tone.ts`), and the
 * dot is never the only channel: the same state is in the accessible name and the tooltip.
 */
const DOT: Record<NonNullable<QueueTabSignal['dot']>, string> = {
  danger: 'bg-danger',
  warning: 'bg-warning',
  info: 'bg-info animate-pulse motion-reduce:animate-none',
};

const tabs = computed(() =>
  sections.value
    .filter((section) => section.key !== 'overview')
    .map((section) => {
      const tabSignal = section.key === 'queue' ? signal.value : null;
      const current = ariaCurrentFor(section, route.name);

      return {
        section,
        icon: ICONS[section.key],
        current,
        active: current !== undefined,
        name: sectionAccessibleName(section, tabSignal),
        signal: tabSignal,
      };
    }),
);

/**
 * The list scrolls sideways with its scrollbar hidden, so on a narrow screen the active tab can
 * start past the edge — Configuración is the last one. Brought into view once, by moving the list
 * only: `scrollIntoView` would scroll the page as well.
 */
const list = useTemplateRef<HTMLUListElement>('list');

onMounted(() => {
  const element = list.value;
  const active = element?.querySelector<HTMLElement>('[aria-current]');

  if (!element || !active) return;

  const overflow = active.getBoundingClientRect().right - element.getBoundingClientRect().right;

  if (overflow > 0) element.scrollLeft += overflow;
});
</script>

<template>
  <!-- The module's background, in the shell's slot for it: on screen exactly while the nav is. -->
  <Teleport defer to="#section-backdrop"><VpBackdrop /></Teleport>

  <nav
    aria-label="Secciones de Virtual Profiler"
    :class="[
      'sticky top-2 z-20 flex max-w-full items-center self-start overflow-hidden rounded-card border shadow-card transition-[padding,gap,background-color] duration-300 ease-out motion-reduce:transition-none',
      onHub
        ? 'w-full flex-wrap gap-4 bg-linear-to-br from-p-accent/10 via-card/75 to-card/75 p-5 backdrop-blur-md'
        : 'w-fit gap-1 bg-background/80 p-1 backdrop-blur',
    ]"
  >
    <!--
      On the hub, the hero card's look: the brand gradient as a thin rule along the top — its one
      appearance on the screen, with no text on it — and a soft glow in the corner.
    -->
    <template v-if="onHub">
      <div aria-hidden="true" class="absolute inset-x-0 top-0 h-1 bg-(image:--gradient-brand)" />
      <div
        aria-hidden="true"
        class="pointer-events-none absolute -top-24 -right-24 size-72 rounded-full bg-radial from-p-accent/15 to-transparent"
      />
    </template>

    <ArTooltip :delay-duration="400">
      <ArTooltipTrigger as-child>
        <RouterLink v-slot="{ href, navigate }" :to="{ name: OVERVIEW.routeName }" custom>
          <a
            :href="href"
            :aria-current="hubCurrent"
            aria-label="Virtual Profiler"
            :class="[
              'relative hidden shrink-0 place-items-center outline-none transition-all duration-300 ease-out focus-visible:ring-[3px] focus-visible:ring-ring/50 motion-reduce:transition-none sm:grid',
              // Lit only on the hub, like a tab: away from it a green icon read as the screen you were on.
              onHub
                ? 'size-12 rounded-card bg-p-accent/10 text-p-accent ring-1 ring-inset ring-p-accent/20 hover:bg-p-accent/15'
                : 'size-8 rounded-control text-muted-foreground hover:bg-accent hover:text-foreground',
            ]"
            @click="navigate"
          >
            <UserSearch
              aria-hidden="true"
              :class="[
                'transition-all duration-300 motion-reduce:transition-none',
                onHub ? 'size-6' : 'size-4',
              ]"
            />
          </a>
        </RouterLink>
      </ArTooltipTrigger>
      <ArTooltipContent side="bottom">Virtual Profiler</ArTooltipContent>
    </ArTooltip>

    <!-- On the hub, what its hero card used to say: the name and whether the screen is live. -->
    <Transition
      enter-active-class="transition duration-300 ease-out motion-reduce:transition-none"
      enter-from-class="-translate-x-2 opacity-0"
    >
      <div v-if="onHub" class="relative flex shrink-0 flex-col gap-1.5">
        <h1 class="t-h2 whitespace-nowrap">Virtual Profiler</h1>
        <div class="flex items-center gap-1.5">
          <ArStatusBadge :tone="live.tone" :label="live.label" />
          <ArInfoTip
            v-if="live.alarm"
            label="Detalle de la conexión"
            :message="`${live.alarm.title}. ${live.alarm.body}`"
          />
        </div>
      </div>
    </Transition>

    <!--
      The height goes through the same data variant ArSeparator uses for `h-full`, so it replaces
      it: a percentage height in a row that is not stretched collapses to nothing.
    -->
    <ArSeparator
      orientation="vertical"
      :class="[
        'relative mx-0.5 hidden sm:block',
        onHub ? 'data-[orientation=vertical]:h-10' : 'data-[orientation=vertical]:h-5',
      ]"
    />

    <ul
      ref="list"
      class="relative flex min-w-0 items-center gap-1 overflow-x-auto [scrollbar-width:none]"
    >
      <li v-for="tab of tabs" :key="tab.section.key">
        <ArTooltip :delay-duration="400">
          <ArTooltipTrigger as-child>
            <RouterLink v-slot="{ href, navigate }" :to="{ name: tab.section.routeName }" custom>
              <a
                :href="href"
                :aria-current="tab.current"
                :aria-label="tab.name"
                :class="[TAB, tab.active ? TAB_ACTIVE : TAB_IDLE]"
                @click="navigate"
              >
                <component
                  :is="tab.icon"
                  aria-hidden="true"
                  :class="['size-4 shrink-0', tab.active && 'text-p-accent']"
                />
                <!--
                  From `xl` and not `md`: the sidebar opens at `md` and takes 16rem, and between
                  the two every label does not fit beside it. Below, the icon, its name and its
                  tooltip carry the tab.
                -->
                <span :class="tab.active ? undefined : 'hidden xl:inline'">{{
                  tab.section.label
                }}</span>
                <span
                  v-if="tab.signal?.count"
                  :class="[
                    PILL,
                    tab.active ? 'bg-background text-foreground' : 'bg-muted text-muted-foreground',
                  ]"
                >
                  {{ formatCount(tab.signal.count) }}
                </span>
                <span
                  v-if="tab.signal?.dot"
                  aria-hidden="true"
                  :class="['size-2 shrink-0 rounded-full', DOT[tab.signal.dot]]"
                />
              </a>
            </RouterLink>
          </ArTooltipTrigger>
          <ArTooltipContent side="bottom">{{ tab.name }}</ArTooltipContent>
        </ArTooltip>
      </li>
    </ul>

    <Transition
      enter-active-class="transition duration-300 ease-out motion-reduce:transition-none"
      enter-from-class="translate-x-2 opacity-0"
    >
      <div v-if="onHub" class="relative ml-auto shrink-0"><HubOptions /></div>
    </Transition>
  </nav>
</template>
